/**
 * Wave F4-D — Admin > CoA Classification.
 *
 * Metadata-driven governance for cash-account detection. Replaces
 * pattern-matching on account_number / system_code with an explicit
 * ``is_cash_account`` flag + ``account_subtype`` (Bank | Cash |
 * PettyCash | AR | AP | ...).
 *
 * Endpoints:
 *   GET  /api/admin/coa/classification
 *   PATCH /api/admin/coa/classification/{account_id}
 *   POST /api/admin/coa/classification/seed
 *   GET  /api/admin/coa/classification/subtypes
 *   GET  /api/admin/coa/classification/audit
 */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Wallet, ShieldCheck, AlertTriangle, RefreshCw, Search, Save,
  Loader2, CheckCircle2, XCircle, Filter, History,
} from 'lucide-react';
import { axiosInstance } from '../App';


const CASH_SUBTYPE_CLS =
  'bg-emerald-100 text-emerald-800 border-emerald-300';
const NON_CASH_CLS =
  'bg-slate-100 text-slate-700 border-slate-300';


export default function AdminCoAClassification() {
  const [subtypes,  setSubtypes]  = useState({ cash: [], all: [] });
  const [accounts,  setAccounts]  = useState([]);
  const [counts,    setCounts]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [query,     setQuery]     = useState('');
  const [filter,    setFilter]    = useState('all');  // all|cash|warning
  const [audit,     setAudit]     = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [editing,   setEditing]   = useState({});    // id → {subtype, is_cash}
  const [misclassified, setMisclassified] = useState([]);
  const [routing,   setRouting]   = useState(null);
  const [selectedFix, setSelectedFix] = useState(new Set());
  const [deprecTarget, setDeprecTarget] = useState('');
  const [usageReport, setUsageReport]   = useState(null);
  const [xferTo, setXferTo] = useState('');
  // Wave (Feb 2026) — Cash-Registry repair + journal repointing.
  const [repairPreview, setRepairPreview] = useState(null);
  const [repointFrom, setRepointFrom] = useState('');
  const [repointTo,   setRepointTo]   = useState('');
  const [repointRefType, setRepointRefType] = useState('expense_report');
  const [repointPlan, setRepointPlan] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [subs, list, misc, route] = await Promise.all([
        axiosInstance.get('/admin/coa/classification/subtypes'),
        axiosInstance.get('/admin/coa/classification'),
        axiosInstance.get('/admin/coa/classification/misclassified').catch(() => ({ data: { misclassified: [] } })),
        axiosInstance.get('/admin/coa/classification/payment-routing').catch(() => ({ data: null })),
      ]);
      setSubtypes({
        cash: subs.data.cash_subtypes,
        all:  subs.data.known_subtypes,
      });
      setAccounts(list.data.accounts);
      setCounts(list.data.counts);
      setMisclassified(misc.data.misclassified || []);
      setRouting(route.data);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  const toggleFixSelect = (id) => {
    setSelectedFix(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runAutoFix = async (dryRun) => {
    if (selectedFix.size === 0) {
      toast.error('اختر حساباً واحداً على الأقل للتصحيح.');
      return;
    }
    if (!dryRun && !window.confirm(
      `Fix ${selectedFix.size} misclassified account(s)?\n\n`
      + `• is_cash_account will be set to false\n`
      + `• is_bank_account will be set to false\n`
      + `• Each change is audited\n\n`
      + `Idempotent — safe to re-run.`)) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        '/admin/coa/classification/fix-misclassified',
        { account_ids: Array.from(selectedFix), dry_run: dryRun });
      if (dryRun) {
        toast.success(`Dry-run — would fix ${r.data.would_fix}`);
      } else {
        toast.success(`Fixed ${r.data.fixed} account(s)`);
        setSelectedFix(new Set());
        await load();
      }
    } catch (e) {
      toast.error(`Fix failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  // ── Wave (Feb 2026) · Cash-Registry auto-repair ──────────────────
  // Unflag rollup / non-cash accounts that were manually ticked as
  // is_cash_account=true (e.g. 11000 Current Assets on production).
  const runRepairCashRegistry = async (dryRun) => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        `/admin/coa/classification/repair-wrongly-flagged-cash?dry_run=${dryRun}`
      );
      if (dryRun) {
        setRepairPreview(r.data);
        if (r.data.unflagged === 0) {
          toast.success('لا توجد حسابات مصنّفة خطأً كنقدية ✔');
        } else {
          toast.info(`سيتم إلغاء تفعيل ${r.data.unflagged} حساب — راجع القائمة أدناه`);
        }
      } else {
        toast.success(`تم إلغاء تفعيل ${r.data.unflagged} حساب.`);
        setRepairPreview(null);
        await load();
      }
    } catch (e) {
      toast.error(`Repair failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  // ── Wave (Feb 2026) · Journal-line repointing ───────────────────
  // For historical journals that hit the wrong cash-ish account
  // (e.g. expense_report crediting 11000 Current Assets), redirect
  // them to the correct account (e.g. 11150 Cash Expenses) in place.
  const planRepoint = async () => {
    if (!repointFrom || !repointTo) {
      toast.error('اختر الحسابين (from + to) أولاً');
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({
        from_account_id: repointFrom,
        to_account_id:   repointTo,
      });
      if (repointRefType) params.append('reference_type', repointRefType);
      const r = await axiosInstance.get(
        `/admin/coa/classification/repoint-plan?${params.toString()}`
      );
      setRepointPlan(r.data);
    } catch (e) {
      toast.error(`Plan failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const executeRepoint = async () => {
    if (!repointPlan) return;
    if (!window.confirm(
      `إعادة توجيه ${repointPlan.count} قيد?\n\n`
      + `من: ${repointPlan.from.account_number} ${repointPlan.from.account_name}\n`
      + `إلى: ${repointPlan.to.account_number} ${repointPlan.to.account_name}\n`
      + `النوع: ${repointPlan.reference_type_filter || 'الكل'}\n\n`
      + `⚠ عملية غير قابلة للتراجع — تُسجَّل في audit log.`
    )) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        '/admin/coa/classification/repoint',
        {
          from_account_id: repointFrom,
          to_account_id:   repointTo,
          reference_type:  repointRefType || null,
          confirm:         true,
        },
      );
      toast.success(
        `تم توجيه ${r.data.journals_touched} قيد `
        + `(${r.data.lines_touched} سطر) من ${r.data.from} → ${r.data.to}`
      );
      setRepointPlan(null);
      setRepointFrom(''); setRepointTo('');
      await load();
    } catch (e) {
      toast.error(`Repoint failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  // Wave F4-J — Account Usage Analyzer / Safe Deprecation / Balance Transfer
  const findAccount = (needle) => {
    const t = (needle || '').trim();
    if (!t) return null;
    return accounts.find(a =>
      a.id === t ||
      String(a.account_number) === t ||
      (a.account_name || '').toLowerCase().includes(t.toLowerCase())
    );
  };

  const analyzeUsage = async () => {
    const acc = findAccount(deprecTarget);
    if (!acc) { toast.error('لم يعثر على الحساب'); return; }
    setBusy(true);
    try {
      const r = await axiosInstance.get(
        `/admin/coa/classification/account-usage?account_id=${acc.id}`);
      setUsageReport(r.data);
    } catch (e) {
      toast.error(`Usage failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const runDeprecate = async (force) => {
    if (!usageReport) return;
    const reason = window.prompt('سبب الإغلاق (سيُوثّق في audit log):',
      'تم استبداله بحساب آخر (Bank-Link SSOT)');
    if (!reason) return;
    setBusy(true);
    try {
      await axiosInstance.post('/admin/coa/classification/deprecate-account',
        { account_id: usageReport.account.id, reason, force });
      toast.success(`تم إغلاق ${usageReport.account.account_number}`);
      setUsageReport(null); setDeprecTarget(''); setXferTo('');
      await load();
    } catch (e) {
      toast.error(`Deprecate failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const runTransfer = async () => {
    if (!usageReport) return;
    const to = findAccount(xferTo);
    if (!to) { toast.error('حدد الحساب الوجهة (to)'); return; }
    const date = window.prompt('تاريخ قيد التحويل (YYYY-MM-DD):',
      new Date().toISOString().slice(0, 10));
    if (!date) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        '/admin/coa/classification/transfer-balance',
        { from_account_id: usageReport.account.id,
           to_account_id:   to.id,
           journal_date:    date,
           reason: `تحويل رصيد من ${usageReport.account.account_number} إلى ${to.account_number}` });
      if (r.data.skipped) {
        toast.info('لا رصيد للنقل');
      } else {
        toast.success(`تم نقل ${r.data.amount} في قيد ${r.data.journal_no}`);
      }
      await analyzeUsage();
    } catch (e) {
      toast.error(`Transfer failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const loadAudit = async () => {
    try {
      const r = await axiosInstance.get('/admin/coa/classification/audit');
      setAudit(r.data.audit);
    } catch (e) {
      toast.error(`Audit load failed: ${e.message}`);
    }
  };

  useEffect(() => { load(); }, []);

  const runSeed = async () => {
    if (!window.confirm(
      'Run one-shot classification seed?\n\n'
      + '• Idempotent — safe to re-run.\n'
      + '• Self-heals accounts whose system_code is in the legacy '
      + 'map (BANK, AP, PETTY_CASH, ...) — overrides mis-flags.\n'
      + '• Operator hand edits on custom codes are preserved.'
    )) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        '/admin/coa/classification/seed');
      const c = r.data.corrected ?? 0;
      if (c > 0) {
        toast.success(
          `Seed OK — ${r.data.updated} updated (${c} corrected)`);
        console.info('CoA seed corrections:', r.data.corrections);
      } else {
        toast.success(
          `Seed OK — updated ${r.data.updated}/${r.data.seen}, `
          + `${r.data.unknown_total} unknown`);
      }
      await load();
    } catch (e) {
      toast.error(`Seed failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const setEdit = (id, field, val) => {
    setEditing(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: val } }));
  };

  const save = async (a) => {
    const patch = editing[a.id];
    if (!patch) return;
    setBusy(true);
    try {
      await axiosInstance.patch(
        `/admin/coa/classification/${a.id}`,
        {
          account_subtype:  patch.account_subtype  ?? a.account_subtype,
          is_cash_account:  patch.is_cash_account  ?? a.is_cash_account,
        });
      toast.success(`Saved ${a.account_name}`);
      setEditing(e => { const x = { ...e }; delete x[a.id]; return x; });
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accounts.filter(a => {
      if (filter === 'cash' && !a.is_cash_account) return false;
      if (filter === 'warning'
          && !(a.used_by_payments && !a.is_cash_account)) return false;
      if (!q) return true;
      return (
        (a.account_number || '').toLowerCase().includes(q)
        || (a.account_name  || '').toLowerCase().includes(q)
        || (a.system_code   || '').toLowerCase().includes(q)
        || (a.account_subtype || '').toLowerCase().includes(q)
      );
    });
  }, [accounts, query, filter]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5"
          data-testid="admin-coa-classification-page">
      <header className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex
                          items-center gap-2">
            <Wallet className="text-indigo-600" size={26} />
            Chart of Accounts — Classification
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Explicit metadata governs cash detection —
            <code className="mx-1">is_cash_account</code> and
            <code className="mx-1">account_subtype</code> replace all
            pattern-matching heuristics. Every module reading cash
            (Reconciliation, Treasury, CFO Dashboard) resolves to this
            single source of truth.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="coa-refresh-btn"
                   onClick={load} disabled={loading}
                   className="inline-flex items-center gap-2 bg-slate-900
                              hover:bg-slate-800 text-white rounded
                              px-3 py-1.5 text-sm font-semibold
                              disabled:opacity-50">
            {loading
              ? <Loader2 size={14} className="animate-spin" />
              : <RefreshCw size={14} />}
            Refresh
          </button>
          <button data-testid="coa-seed-btn"
                   onClick={runSeed} disabled={busy}
                   title="Auto-classify unclassified accounts using legacy system_code"
                   className="inline-flex items-center gap-2 bg-indigo-600
                              hover:bg-indigo-700 text-white rounded
                              px-3 py-1.5 text-sm font-semibold
                              disabled:opacity-50">
            <ShieldCheck size={14} /> Run Seed
          </button>
          <button data-testid="coa-audit-btn"
                   onClick={() => { setShowAudit(v => !v); if (!showAudit) loadAudit(); }}
                   className="inline-flex items-center gap-2 bg-slate-100
                              hover:bg-slate-200 text-slate-800 rounded
                              px-3 py-1.5 text-sm font-semibold">
            <History size={14} /> Audit
          </button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="coa-kpis">
        {[
          ['Total Accounts',       counts.total,           'text-slate-900'],
          ['Cash Accounts',        counts.cash,            'text-emerald-700'],
          ['Used by Payments',     counts.used_by_payments, 'text-indigo-700'],
          ['⚠ Non-cash on Payments', counts.warning_payments_using_non_cash,
                                                            'text-rose-700'],
        ].map(([k, v, cls]) => (
          <div key={k} className="bg-white border border-slate-200
                                    rounded-lg p-3">
            <div className="text-[10px] uppercase font-semibold text-slate-500">
              {k}
            </div>
            <div className={`text-2xl font-bold mt-1 ${cls}`}>{v ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* STA-7.2 — Essential Classification Guide */}
      <details className="bg-white border-2 border-indigo-200 rounded-lg overflow-hidden"
               data-testid="coa-setup-guide">
        <summary className="cursor-pointer bg-indigo-50 px-4 py-3 font-bold text-indigo-900 flex items-center gap-2 select-none">
          <ShieldCheck size={16} />
          دليل الحسابات الأساسية — أي حسابات يجب تفعيلها ليعمل النظام 100%؟
        </summary>
        <div className="p-4 space-y-4 text-right" dir="rtl">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-sm font-bold text-emerald-900 mb-2">1. حسابات النقدية والبنك (Assets · فعِّل is_cash_account = true)</div>
            <ul className="text-xs space-y-1 text-emerald-900 mr-4 list-disc">
              <li><b>11000</b> — Cash on Hand / نقدية</li>
              <li><b>11100</b> — Bank Main / بنك رئيسي (وأي حساب بنكي آخر: 11101, 11102, ...)</li>
              <li><b>11200</b> — Petty Cash / نثريات</li>
              <li><b>11500</b> — Cash Equivalents / بدائل نقدية</li>
            </ul>
            <div className="text-[10px] text-emerald-700 mt-2 italic">القاعدة المحاسبية: فقط الأصول (Assets). لا يمكن تصنيف حسابات الخصوم/الإيرادات/المصاريف كنقدية.</div>
          </div>

          <div className="bg-sky-50 border border-sky-200 rounded p-3">
            <div className="text-sm font-bold text-sky-900 mb-2">2. الحسابات الحرجة (System Code — لا تحتاج is_cash_account)</div>
            <ul className="text-xs space-y-1 text-sky-900 mr-4 list-disc">
              <li><b>11300</b> — VAT Input (Recoverable) · system_code=VAT_INPUT · <span className="text-slate-700">أصول</span></li>
              <li><b>12000-12999</b> — Accounts Receivable (AR) · system_code=ACCOUNTS_RECEIVABLE · <span className="text-slate-700">أصول</span></li>
              <li><b>21000-21099</b> — Accounts Payable (AP) · system_code=ACCOUNTS_PAYABLE · <span className="text-slate-700">خصوم</span></li>
              <li><b>21300</b> — VAT Output (Payable) · system_code=VAT_OUTPUT · <span className="text-slate-700">خصوم</span> · <span className="font-bold text-rose-700">ليس نقدية!</span></li>
              <li><b>21310</b> — VAT Payable Net · system_code=VAT_PAYABLE</li>
              <li><b>21500</b> — Retention Payable · system_code=RETENTION_PAYABLE</li>
              <li><b>22000</b> — Advance Payments Received</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-sm font-bold text-amber-900 mb-2">3. الحسابات التشغيلية</div>
            <ul className="text-xs space-y-1 text-amber-900 mr-4 list-disc">
              <li><b>41000-41999</b> — Revenue (المبيعات، الإيرادات المتنوعة)</li>
              <li><b>51000-51999</b> — Direct Costs (المشاريع — يحتاج project_id على القيود)</li>
              <li><b>52000-52999</b> — G&A Overhead (يجمع بلا project_id)</li>
              <li><b>53000-53999</b> — Allocated Overhead (يظهر بعد تشغيل Overhead Scenarios)</li>
            </ul>
          </div>

          <div className="bg-rose-50 border-2 border-rose-300 rounded p-3">
            <div className="text-sm font-bold text-rose-900 mb-1">⚠ سيناريو الخطأ الذي رأيتَه (21300 VAT Output)</div>
            <p className="text-xs text-rose-900 leading-relaxed">
              محاولة تصنيف <code className="bg-white px-1 rounded">21300 · VAT Output</code> كنقدية مرفوضة قواعدياً — 
              لأن VAT Output هو <b>خصم (Liability)</b> يمثّل الضريبة المستحقة على المبيعات، وليس رصيداً نقدياً. 
              الآن — بعد هذا الإصلاح — checkbox معطَّل تلقائياً لأي حساب غير أصول (Assets)، ولن ترى هذا الخطأ مرة أخرى.
            </p>
          </div>
        </div>
      </details>

      {/* Wave F4-H — Misclassified Detection Banner */}
      {misclassified.length > 0 && (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-lg overflow-hidden"
             data-testid="misclassified-panel">
          <div className="bg-rose-100 px-4 py-3 border-b border-rose-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-rose-700" size={20} />
              <div className="flex-1">
                <div className="font-bold text-rose-900">
                  ⚠️ {misclassified.length} حساب غير-Asset مُعلَّم كنقد (خرق لقاعدة الأعمال)
                </div>
                <div className="text-xs text-rose-700">
                  فقط حسابات Asset يمكن أن تكون Cash. المطلوبات/الإيرادات/المصروفات لا يجوز تصنيفها كنقد
                  لأنها تفسد كل تسوية Cash ↔ Payments.
                </div>
              </div>
              <button
                data-testid="autofix-dryrun-btn"
                onClick={() => runAutoFix(true)}
                disabled={busy || selectedFix.size === 0}
                className="bg-slate-600 hover:bg-slate-700 text-white rounded
                            px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                Dry-Run
              </button>
              <button
                data-testid="autofix-commit-btn"
                onClick={() => runAutoFix(false)}
                disabled={busy || selectedFix.size === 0}
                className="bg-rose-600 hover:bg-rose-700 text-white rounded
                            px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                Fix Selected ({selectedFix.size})
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-rose-50/60 text-[11px] uppercase text-rose-700">
              <tr>
                <th className="px-3 py-2 text-center w-8">
                  <input type="checkbox"
                    checked={selectedFix.size === misclassified.length && misclassified.length > 0}
                    onChange={(e) => setSelectedFix(e.target.checked
                      ? new Set(misclassified.map(m => m.account_id))
                      : new Set())} />
                </th>
                <th className="px-3 py-2 text-left">Account #</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {misclassified.map(m => (
                <tr key={m.account_id} className="border-t border-rose-100">
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox"
                      data-testid={`fix-check-${m.account_number}`}
                      checked={selectedFix.has(m.account_id)}
                      onChange={() => toggleFixSelect(m.account_id)} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{m.account_number}</td>
                  <td className="px-3 py-2">{m.account_name}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 rounded-full
                                     bg-rose-100 text-rose-700 border border-rose-300
                                     text-[10px] font-bold uppercase">
                      {m.account_type || 'UNSET'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">{m.reason}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-rose-700">
                    {m.suggested_action}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Wave F4-H — Payment Routing Report */}
      {routing?.cash_accounts?.length > 0 && (
        <div className="bg-white border border-indigo-200 rounded-lg overflow-hidden"
             data-testid="payment-routing-panel">
          <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200">
            <div className="font-bold text-indigo-900">
              🔀 Payment Routing Report — أي وحدة تستخدم أي حساب نقدي؟
            </div>
            <div className="text-xs text-indigo-700 mt-1">
              يكشف الحالة التي تعمل فيها Payments module و Bills module على حسابين
              GL مختلفين رغم أنهما لبنك واحد فعلياً.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Account</th>
                <th className="px-3 py-2 text-right">Total Debit</th>
                <th className="px-3 py-2 text-right">Total Credit</th>
                <th className="px-3 py-2 text-left">Modules Touching</th>
                <th className="px-3 py-2 text-left">Linked Banks</th>
              </tr>
            </thead>
            <tbody>
              {routing.cash_accounts.map(a => (
                <tr key={a.account_id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-semibold">{a.account_name}</div>
                    <div className="font-mono text-xs text-slate-500">{a.account_number}</div>
                    <div className="mt-1 flex gap-1">
                      {a.classification.in_bank_linked_set && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded
                                         bg-emerald-100 text-emerald-700 border border-emerald-300 font-bold">
                          Bank-linked
                        </span>
                      )}
                      {a.classification.in_flagged_set && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded
                                         bg-indigo-100 text-indigo-700 border border-indigo-300 font-bold">
                          Flagged
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{a.total_debit.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono">{a.total_credit.toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-0.5">
                      {a.modules_touching.map(m => (
                        <div key={m.ref_type} className="flex items-center gap-2 text-xs">
                          <span className="font-mono px-1.5 py-0.5 rounded
                                           bg-slate-100 text-slate-700 border border-slate-200">
                            {m.ref_type}
                          </span>
                          <span className="text-slate-500">×{m.count}</span>
                          <span className="text-emerald-600 font-mono">Dr {m.debit.toLocaleString()}</span>
                          <span className="text-rose-600 font-mono">Cr {m.credit.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {a.linked_bank_accounts.length === 0
                      ? <span className="text-rose-600 font-bold">— لا يوجد —</span>
                      : a.linked_bank_accounts.map(b => (
                          <div key={b.id}>{b.name}</div>
                        ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 border border-slate-300
                        rounded overflow-hidden">
          {['all', 'cash', 'warning'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
                     data-testid={`coa-filter-${f}`}
                     className={`px-3 py-1.5 text-xs font-semibold
                                  ${filter === f
                          ? 'bg-slate-900 text-white'
                          : 'bg-white hover:bg-slate-100 text-slate-700'}`}>
              {f === 'all' ? 'All'
                : f === 'cash' ? 'Cash Only'
                : '⚠ Warnings'}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <input
            data-testid="coa-search"
            type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search account number / name / subtype…"
            className="border border-slate-300 rounded pl-7 pr-3 py-1.5 text-sm w-full"
          />
        </div>
      </div>

      {/* Wave (Feb 2026) — Cash-Registry Repair + Journal Repointing */}
      <div className="bg-white border-2 border-rose-300 rounded-lg overflow-hidden"
            data-testid="cash-repair-panel">
        <div className="bg-rose-50 px-4 py-3 border-b border-rose-200">
          <div className="font-bold text-rose-900">
            🛠 إصلاح Cash Registry — إلغاء تفعيل الحسابات غير النقدية
          </div>
          <div className="text-xs text-rose-700 mt-1">
            يبحث عن حسابات مُعلَّمة <code>is_cash_account=true</code> خطأً (مثل
            <b> 11000 Current Assets </b> أو حسابات rollup)، ثم يُلغي التفعيل
            تلقائياً. الحسابات النقدية الشرعية (Bank/Cash/PettyCash/CASH_EXPENSES
            أو أي حساب مرتبط ببنك) لا تُلمس.
          </div>
        </div>
        <div className="p-4 flex flex-wrap gap-2 items-center">
          <button
            data-testid="repair-cash-preview-btn"
            onClick={() => runRepairCashRegistry(true)}
            disabled={busy}
            className="px-3 py-1.5 bg-slate-200 text-slate-800 rounded text-sm font-semibold disabled:opacity-50"
          >
            🔍 معاينة (Dry-Run)
          </button>
          <button
            data-testid="repair-cash-execute-btn"
            onClick={() => runRepairCashRegistry(false)}
            disabled={busy}
            className="px-3 py-1.5 bg-rose-600 text-white rounded text-sm font-semibold disabled:opacity-50"
          >
            🛠 تنفيذ الإصلاح
          </button>
          {repairPreview && (
            <div data-testid="repair-preview-summary"
                  className="ml-auto text-sm text-rose-800">
              سيتم إلغاء تفعيل <b>{repairPreview.unflagged}</b> حساب
            </div>
          )}
        </div>
        {repairPreview && repairPreview.accounts?.length > 0 && (
          <div className="border-t border-rose-100 px-4 py-2">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-start">الرقم</th>
                  <th className="text-start">الاسم</th>
                  <th className="text-start">Subtype</th>
                  <th className="text-start">System Code</th>
                </tr>
              </thead>
              <tbody>
                {repairPreview.accounts.map(a => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="py-1 font-mono">{a.account_number}</td>
                    <td className="py-1">{a.account_name}</td>
                    <td className="py-1 text-slate-500">{a.account_subtype}</td>
                    <td className="py-1 text-slate-500">{a.system_code || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Repoint tool — for historical journals stuck on the wrong account. */}
        <div className="bg-slate-50 border-t border-rose-200 px-4 py-3">
          <div className="font-bold text-slate-800 mb-1">
            ♻ إعادة توجيه القيود التاريخية (Journal Repointing)
          </div>
          <div className="text-xs text-slate-600 mb-3">
            إذا كانت القيود القديمة تنزل على الحساب الخطأ (مثال:
            <code> expense_report → 11000 Current Assets </code>)،
            استخدم هذه الأداة لتحويلها إلى الحساب الصحيح
            (مثل <code> 11150 Cash Expenses </code>). سطر واحد لكل قيد يُعاد كتابته
            في مكانه، مع تسجيل audit log كامل.
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
            <div>
              <label className="text-xs text-slate-600 block mb-1">من حساب (From)</label>
              <select
                data-testid="repoint-from-select"
                value={repointFrom}
                onChange={e => setRepointFrom(e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="">— اختر —</option>
                {accounts.filter(a => a.account_type === 'assets' || a.account_type === 'liabilities').map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_number} · {a.account_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">إلى حساب (To)</label>
              <select
                data-testid="repoint-to-select"
                value={repointTo}
                onChange={e => setRepointTo(e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="">— اختر —</option>
                {accounts.filter(a => a.account_type === 'assets').map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_number} · {a.account_name}
                    {a.is_cash_account ? ' 🟢' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">
                Reference type (اختياري)
              </label>
              <select
                data-testid="repoint-ref-type-select"
                value={repointRefType}
                onChange={e => setRepointRefType(e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="">— الكل —</option>
                <option value="expense_report">expense_report</option>
                <option value="expense_report_reversal">expense_report_reversal</option>
                <option value="expense">expense</option>
                <option value="payment">payment</option>
                <option value="supplier_bill">supplier_bill</option>
                <option value="invoice">invoice</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                data-testid="repoint-plan-btn"
                onClick={planRepoint}
                disabled={busy || !repointFrom || !repointTo}
                className="px-3 py-1.5 bg-slate-700 text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                🔍 خطة
              </button>
              <button
                data-testid="repoint-execute-btn"
                onClick={executeRepoint}
                disabled={busy || !repointPlan || repointPlan?.count === 0}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                ♻ نفّذ
              </button>
            </div>
          </div>
          {repointPlan && (
            <div data-testid="repoint-plan-summary"
                  className="mt-3 p-3 rounded bg-white border border-slate-200 text-sm">
              <div className="font-semibold">
                {repointPlan.count > 0
                  ? `📋 سيتم توجيه ${repointPlan.count} قيد`
                  : '✅ لا توجد قيود مطابقة — الحساب المصدر نظيف'}
              </div>
              {repointPlan.count > 0 && (
                <>
                  <div className="text-xs text-slate-600 mt-1">
                    Debit: <b>{Number(repointPlan.total_debit).toLocaleString()}</b> —
                    Credit: <b>{Number(repointPlan.total_credit).toLocaleString()}</b>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {repointPlan.from.account_number} → {repointPlan.to.account_number}
                    {repointPlan.reference_type_filter ? ` · ${repointPlan.reference_type_filter}` : ''}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Wave F4-J — Deprecation Tools Panel */}
      <div className="bg-white border-2 border-amber-300 rounded-lg overflow-hidden"
            data-testid="deprecation-tools-panel">
        <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
          <div className="font-bold text-amber-900">
            🔒 Account Deprecation Tools — إغلاق الحسابات القديمة بأمان
          </div>
          <div className="text-xs text-amber-700 mt-1">
            حلّل استخدام أي حساب أولاً قبل إغلاقه. الأداة تفحص journals + bank_accounts +
            expense_categories + company_profile + bank_matching_rules. الرصيد يُنقل بقيد واحد
            (Dr وجهة / Cr مصدر).
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase text-slate-600 font-semibold">
              الحساب المراد فحصه
            </label>
            <input value={deprecTarget} onChange={e => setDeprecTarget(e.target.value)}
              placeholder="رقم الحساب أو الاسم أو ID"
              data-testid="deprec-target-input"
              className="w-full mt-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={analyzeUsage} disabled={busy || !deprecTarget}
              data-testid="deprec-analyze-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded
                          px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
              🔍 Analyze Usage
            </button>
          </div>
        </div>

        {usageReport && (
          <div className="border-t border-amber-200 bg-white">
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div><span className="text-slate-500 text-xs">Account:</span>
                <div className="font-bold">{usageReport.account.account_number} · {usageReport.account.account_name}</div>
              </div>
              <div><span className="text-slate-500 text-xs">Type / Subtype:</span>
                <div>{usageReport.account.account_type} / {usageReport.account.account_subtype || '—'}</div>
              </div>
              <div><span className="text-slate-500 text-xs">Current Balance:</span>
                <div className="font-mono font-bold">{usageReport.account.current_balance?.toLocaleString()}</div>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-left">Detail</th>
                </tr>
              </thead>
              <tbody>
                {usageReport.sources.map(s => (
                  <tr key={s.source} className="border-t border-slate-100">
                    <td className="px-3 py-2">{s.source}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${
                      s.count === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {s.count}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {Array.isArray(s.detail) ? s.detail.join(', ') : (s.detail || '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={`px-4 py-3 border-t border-slate-200 flex items-center gap-3 flex-wrap ${
              usageReport.safe_to_deprecate ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold
                                 ${usageReport.safe_to_deprecate
                                     ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                     : 'bg-amber-100 text-amber-800 border border-amber-300'}`}
                    data-testid="deprec-verdict">
                Verdict: {usageReport.verdict}
              </span>

              {/* Deprecated status label — no longer hides Transfer button
                   because balance_transfer is on the whitelist and MUST
                   remain available to drain the closed account. */}
              {usageReport.account.is_deprecated && (
                <span className="text-rose-700 font-bold text-sm"
                      data-testid="deprec-status-badge">
                  🔒 Deprecated ({usageReport.account.deprecated_at?.slice(0, 10)})
                </span>
              )}

              {/* Transfer Balance — visible whenever balance != 0,
                   even on deprecated accounts (whitelisted). */}
              {Math.abs(usageReport.account.current_balance || 0) >= 0.01 && (
                <>
                  <input value={xferTo} onChange={e => setXferTo(e.target.value)}
                    placeholder="Transfer balance to (رقم الحساب)"
                    data-testid="xfer-to-input"
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <button onClick={runTransfer} disabled={busy || !xferTo}
                    data-testid="deprec-xfer-btn"
                    className="bg-slate-700 hover:bg-slate-800 text-white rounded
                                px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                    💸 Transfer Balance
                  </button>
                </>
              )}

              {/* Deprecate buttons — only for accounts NOT yet deprecated. */}
              {!usageReport.account.is_deprecated && (
                <>
                  <button onClick={() => runDeprecate(false)}
                    disabled={busy || !usageReport.safe_to_deprecate}
                    data-testid="deprec-btn"
                    className="bg-rose-600 hover:bg-rose-700 text-white rounded
                                px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                    🔒 Deprecate
                  </button>
                  <button onClick={() => runDeprecate(true)} disabled={busy}
                    data-testid="deprec-force-btn"
                    className="bg-rose-800 hover:bg-rose-900 text-white rounded
                                px-3 py-1.5 text-sm font-semibold disabled:opacity-40">
                    ⚠️ Deprecate (Force)
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
            data-testid="coa-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase text-slate-600">
              <th className="px-3 py-2">Account #</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">System Code</th>
              <th className="px-3 py-2">Subtype</th>
              <th className="px-3 py-2 text-center">Is Cash?</th>
              <th className="px-3 py-2">Used by Payments</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => {
              const isCash  = (editing[a.id]?.is_cash_account ?? a.is_cash_account);
              const subtype = editing[a.id]?.account_subtype ?? a.account_subtype;
              const dirty   = !!editing[a.id];
              // Feb 2026 · Warning refinement — suppress the red highlight
              // for accounts that are used-by-payments as the legitimate
              // ACCRUAL leg (AR/AP/VAT/etc.). ``is_expected_non_cash`` is
              // stamped by the backend based on ``account_subtype``.
              const warn    = a.used_by_payments
                              && !a.is_cash_account
                              && !a.is_expected_non_cash
                              && !dirty;
              return (
                <tr key={a.id} data-testid={`coa-row-${a.id}`}
                    className={`border-t border-slate-100
                                  ${dirty
                        ? 'bg-amber-50'
                        : warn ? 'bg-rose-50' : ''}`}>
                  <td className="px-3 py-2 font-mono">{a.account_number}</td>
                  <td className="px-3 py-2">{a.account_name}</td>
                  <td className="px-3 py-2 text-xs">{a.account_type}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {a.system_code || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`coa-subtype-${a.id}`}
                      value={subtype || ''}
                      onChange={e => setEdit(a.id, 'account_subtype',
                                                 e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1
                                    text-xs font-mono w-full max-w-xs">
                      <option value="">—</option>
                      {subtypes.all.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {(() => {
                      const isAssetAccount = (a.account_type || '').toLowerCase() === 'assets'
                        || (a.account_type || '').toLowerCase() === 'asset';
                      const canBeCash = isAssetAccount || a.is_cash_account;
                      const disabledReason = a.linked_from_bank_account
                        ? 'Auto-classified from a Bank Account record. Change the bank record instead.'
                        : !isAssetAccount && !a.is_cash_account
                          ? `لا يمكن تصنيف هذا الحساب كنقدية — نوعه ${a.account_type}. القاعدة المحاسبية: فقط حسابات الأصول (Assets) يمكن أن تكون نقدية.`
                          : null;
                      return (
                        <label className={`inline-flex items-center gap-1 ${canBeCash && !a.linked_from_bank_account ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                               title={disabledReason || ''}>
                          <input
                            data-testid={`coa-is-cash-${a.id}`}
                            type="checkbox"
                            checked={!!isCash}
                            disabled={a.linked_from_bank_account || (!isAssetAccount && !a.is_cash_account)}
                            onChange={e => setEdit(a.id, 'is_cash_account',
                                                   e.target.checked)}
                          />
                          {a.linked_from_bank_account
                            ? <span className="text-[10px] px-1.5 py-0.5 rounded
                                                border bg-indigo-100 text-indigo-800
                                                border-indigo-300"
                                    title="Auto-classified — a Bank Account record
                                            points at this GL account via
                                            linked_account_id.">
                                BANK-LINKED 🔗
                              </span>
                            : !isAssetAccount && !a.is_cash_account
                              ? <span className="text-[10px] px-1.5 py-0.5 rounded
                                                  border bg-slate-200 text-slate-500
                                                  border-slate-300"
                                      title={disabledReason}>
                                  N/A
                                </span>
                              : isCash
                                ? <span className={`text-[10px] px-1.5 py-0.5 rounded
                                                    border ${CASH_SUBTYPE_CLS}`}>
                                    CASH
                                  </span>
                                : <span className={`text-[10px] px-1.5 py-0.5 rounded
                                                    border ${NON_CASH_CLS}`}>
                                    —
                                  </span>}
                        </label>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2">
                    {a.used_by_payments
                      ? <span className="inline-flex items-center gap-1
                                          text-indigo-700 text-xs font-semibold">
                          <CheckCircle2 size={12} /> Yes
                          {warn && (
                            <span
                              className="ml-1 text-rose-700 inline-flex
                                                items-center gap-0.5"
                              title="حساب Asset يستخدم في مدفوعات لكنه غير مُصنَّف كنقدية —
                                    قد يكون نقصاً في التصنيف. راجع الحساب."
                            >
                              <AlertTriangle size={12} />
                              Non-cash!
                            </span>
                          )}
                          {a.is_expected_non_cash && (
                            <span
                              className="ml-1 text-slate-500 text-[10px]"
                              title="حساب استحقاق (Accrual) — يظهر على قيود المدفوعات
                                    كطرف مقابل بشكل طبيعي (AR/AP/VAT). ليس خطأ."
                            >
                              (accrual)
                            </span>
                          )}
                        </span>
                      : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {dirty && (
                      <button data-testid={`coa-save-${a.id}`}
                               onClick={() => save(a)} disabled={busy}
                               className="inline-flex items-center gap-1
                                            bg-emerald-600 hover:bg-emerald-700
                                            text-white rounded px-2 py-1
                                            text-xs font-semibold
                                            disabled:opacity-50">
                        <Save size={11} /> Save
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center
                                              text-slate-400">
                No accounts match your filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Audit log */}
      {showAudit && (
        <div className="bg-white border border-slate-200 rounded-lg
                         overflow-hidden" data-testid="coa-audit">
          <div className="bg-slate-50 px-4 py-2 text-xs uppercase font-semibold
                          text-slate-600">
            Change Audit Log (last {audit.length})
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="px-3 py-1">When</th>
                <th className="px-3 py-1">Actor</th>
                <th className="px-3 py-1">Account</th>
                <th className="px-3 py-1">Before</th>
                <th className="px-3 py-1">After</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1 font-mono">
                    {(a.at || '').slice(0, 19)}
                  </td>
                  <td className="px-3 py-1">{a.actor_name}</td>
                  <td className="px-3 py-1 font-mono">
                    {a.account_number}
                  </td>
                  <td className="px-3 py-1">
                    <code>{JSON.stringify(a.before)}</code>
                  </td>
                  <td className="px-3 py-1">
                    <code>{JSON.stringify(a.after)}</code>
                  </td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center
                                                text-slate-400">
                  No audit entries yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
